import { service } from '@ember/service';
import { t } from 'ember-intl';
import Button from 'tio-ui/components/button';
import Card from 'tio-ui/components/card';
import Component from '@glimmer/component';
import type { TOC } from '@ember/component/template-only';
import Heading from 'tio-ui/components/heading';
import QualifyingPaymentTracker from 'tio-employee/components/pslf/dashboard/qualifying-payment-tracker';
import Text from 'tio-ui/components/text';
import transitionTo from 'ember-router-helpers/helpers/transition-to';
import { or } from 'tio-ui/utilities';
import PageHero from 'tio-ui/components/page-hero';
import { VStack } from 'tio-ui/components/layout';
import { PhHandCoins, PhArrowRight, PhWarningCircle, PhGear } from 'ember-phosphor-icons';
import AnnualCountdownCard from 'tio-employee/components/pslf/annual-auto-submit/countdown-card';
import AnnualSettingsDialog from 'tio-employee/components/pslf/annual-auto-submit/settings-dialog';
import { tracked } from '@glimmer/tracking';
import type { AnnualAutoSubmitStatus } from 'tio-employee/utils/pslf/annual-auto-submit';
import type { IntlService } from 'ember-intl';
import type RouterService from '@ember/routing/router-service';
import type { LoanSummary } from 'tio-common/types/loan-summary';
import type { PslfFormExtension as PslfFormModel } from 'tio-data/data/resources/pslf-form.ext';
import type { PslfProfileExtension as PslfProfileModel } from 'tio-data/data/resources/pslf-profile.ext';
import type { PersonExtension as PersonModel } from 'tio-data/data/resources/person.ext';

type HeroState = 'no-nslds' | 'has-nslds' | 'annual-reminder' | 'annual-countdown';

// The forgiveness estimator is the sort-plans flow reached at its own route (no
// longer an in-hero modal).
const ESTIMATOR_ROUTE = 'authenticated.pslf.dashboard.forgiveness-estimator';

/** The countdown card runs tall, so the tracker beside it can show more loans before folding. */
const COUNTDOWN_VISIBLE_LOANS = 4;

interface UploadCtaCardSignature {
  Args: {
    queryParams: Record<string, string>;
  };
  Element: HTMLDivElement;
}

const UploadCtaCard: TOC<UploadCtaCardSignature> = <template>
  <Card @padding="lg" class="tio-col-flush" data-test-upload-cta-card ...attributes>
    <VStack class="p-0">
      <Text @variant="muted" @size="xs" @weight="semibold" class="uppercase tracking-wider">
        {{t "pslf.reimagined.hero.upload_next_step"}}
      </Text>
      <Heading @level={{3}} @size="base">
        {{t "pslf.reimagined.hero.upload_title"}}
      </Heading>
      <Text @variant="muted" @size="sm">
        {{t "pslf.reimagined.hero.upload_description"}}
      </Text>
    </VStack>
    <Button
      class="mt-3 self-start"
      @appearance="minimal"
      @intent="primary"
      data-test-upload-data-link
      {{on
        "click"
        (transitionTo "authenticated.observability.upload" (hash queryParams=@queryParams))
      }}
    >
      {{t "pslf.reimagined.hero.upload_cta"}}
      <PhArrowRight @size={{16}} />
    </Button>
  </Card>
</template>;

export interface HeroDashboardSignature {
  Args: {
    pslfProfile: PslfProfileModel;
    pslfForms: PslfFormModel[];
    loanSummary: LoanSummary;
    person: PersonModel;
    // Debug/testing override (?showEstimator=true): force the estimator tile to
    // render regardless of the usual gating.
    forceEstimator?: boolean;
    /** Omitted (or inactive) while `pslfAnnualAutoSubmit` is off, which keeps the pre-feature hero. */
    annualAutoSubmit?: AnnualAutoSubmitStatus;
  };
  Element: HTMLDivElement;
}

export default class HeroDashboard extends Component<HeroDashboardSignature> {
  @service declare intl: IntlService;
  @service declare router: RouterService;

  @tracked isSettingsOpen = false;

  /** The countdown states only; the settings control has its own, looser gate. */
  get annualAutoSubmit() {
    const status = this.args.annualAutoSubmit;
    return status?.isActive ? status : undefined;
  }

  /** Reachable whenever the job could file, even with no anniversary to count toward. */
  get annualSettings() {
    const status = this.args.annualAutoSubmit;
    return status?.available ? status : undefined;
  }

  openSettings = () => {
    this.isSettingsOpen = true;
  };

  closeSettings = () => {
    this.isSettingsOpen = false;
  };

  get greeting() {
    const hour = new Date().getHours();
    const firstName =
      this.args.person?.firstName?.trim() || this.intl.t('slr.reimagined.default_name');

    let timeGreeting: string;
    if (hour < 12) {
      timeGreeting = this.intl.t('slr.reimagined.good_morning');
    } else if (hour < 17) {
      timeGreeting = this.intl.t('slr.reimagined.good_afternoon');
    } else {
      timeGreeting = this.intl.t('slr.reimagined.good_evening');
    }

    return `${timeGreeting}, ${firstName}`;
  }

  get hasNsldsData() {
    const accounts = this.args.person?.accounts ?? [];
    return accounts.some((account) => account.hasNsldsPslfData);
  }

  get mostRecentCurrentEmployerForm() {
    const forms = this.args.pslfForms ?? [];
    const currentEmployerForms = forms
      .filter((f) => f.isCurrentEmployer && f.isSubmitted && f.submittedAt)
      .sort((a, b) => new Date(b.submittedAt!).getTime() - new Date(a.submittedAt!).getTime());

    return currentEmployerForms[0] ?? null;
  }

  get needsAnnualReminder() {
    const form = this.mostRecentCurrentEmployerForm;
    if (!form?.submittedAt) return false;

    const submittedDate = new Date(form.submittedAt);
    const now = new Date();
    const monthsDiff =
      (now.getFullYear() - submittedDate.getFullYear()) * 12 +
      (now.getMonth() - submittedDate.getMonth());

    return monthsDiff >= 11;
  }

  /** Days remaining until 12 months from the most recent form's submittedAt. 0 or negative means it's due now. */
  get annualReminderDaysRemaining(): number {
    const form = this.mostRecentCurrentEmployerForm;
    if (!form?.submittedAt) return 0;

    const submittedDate = new Date(form.submittedAt);
    const dueDate = new Date(submittedDate);
    dueDate.setFullYear(dueDate.getFullYear() + 1);

    const now = new Date();
    const diffMs = dueDate.getTime() - now.getTime();
    return Math.max(0, Math.ceil(diffMs / (1000 * 60 * 60 * 24)));
  }

  get isAnnualFormDueNow() {
    return this.annualReminderDaysRemaining === 0;
  }

  get heroState(): HeroState {
    // The override forces the has-nslds layout (tracker + estimator tile).
    if (this.args.forceEstimator) return 'has-nslds';
    // With the feature live the countdown owns the annual window outright, and outside
    // it the alert strip carries the due date — so the legacy reminder never doubles up.
    // An employee whose own form is in flight gets neither: both would start a second one.
    if (this.annualAutoSubmit) {
      if (this.annualAutoSubmit.isWithinWindow) return 'annual-countdown';
    } else if (this.needsAnnualReminder && !this.args.annualAutoSubmit?.manualFormInProgress) {
      return 'annual-reminder';
    }
    if (this.hasNsldsData) return 'has-nslds';
    return 'no-nslds';
  }

  get isAnnualReminder() {
    return this.heroState === 'annual-reminder';
  }

  get isAnnualCountdown() {
    return this.heroState === 'annual-countdown';
  }

  get isNoNslds() {
    return this.heroState === 'no-nslds';
  }

  goToEstimator = () => {
    this.router.transitionTo(ESTIMATOR_ROUTE);
  };

  get hasPslfForms() {
    return (this.args.pslfForms?.length ?? 0) > 0;
  }

  get uploadQueryParams() {
    return { source: 'pslf', providers: 'nslds' };
  }

  <template>
    {{#if (or this.hasPslfForms @forceEstimator)}}
      <PageHero
        @gradient="brand-soft"
        class="tio-fg-primary tio-pslf-hero-icon tio-elevation-card-raised"
        data-test-hero-dashboard
        ...attributes
      >
        <:icon>
          <PhHandCoins @size={{20}} />
          <Text @variant="body" @size="sm" @weight="semibold" class="tio-fg-brand">
            {{this.greeting}}
          </Text>
        </:icon>
        <:default>
          <div class="tio-row-between flex-wrap gap-3 mb-5">
            <Heading @level={{1}}>
              {{t "pslf.reimagined.hero.journey_heading"}}
            </Heading>
            {{#if this.annualSettings}}
              <Button
                @appearance="outlined"
                @intent="primary"
                @size="sm"
                @icon={{PhGear}}
                @ariaLabel={{t "pslf.reimagined.annual_auto_submit.settings.heading"}}
                @testHook="pslf-hero__settings"
                data-test-pslf-settings-button
                {{on "click" this.openSettings}}
              >
                {{t "pslf.reimagined.annual_auto_submit.settings.trigger"}}
              </Button>
            {{/if}}
          </div>

          <div class="grid grid-cols-1 md:grid-cols-2 gap-4">

            {{#if this.isAnnualCountdown}}
              {{#if this.annualAutoSubmit}}
                <AnnualCountdownCard
                  @status={{this.annualAutoSubmit}}
                  @onManageDetails={{if this.annualSettings this.openSettings}}
                />
              {{/if}}

              {{#if this.hasNsldsData}}
                <QualifyingPaymentTracker
                  @accounts={{@person.accounts}}
                  @visibleLoanCount={{COUNTDOWN_VISIBLE_LOANS}}
                />
              {{else}}
                <UploadCtaCard @queryParams={{this.uploadQueryParams}} class="self-start" />
              {{/if}}

            {{else if this.isAnnualReminder}}
              <Card @padding="lg" class="tio-col" data-test-annual-reminder-card>
                <div class="tio-row-snug">
                  <PhWarningCircle @size={{16}} class="tio-fg-danger" />
                  <Text @size="sm" @weight="semibold" class="tio-fg-danger">
                    {{t "pslf.reimagined.hero.annual_reminder_badge"}}
                  </Text>
                </div>
                <Heading @level={{3}} @size="base">
                  {{t "pslf.reimagined.hero.annual_reminder_title"}}
                </Heading>
                {{#if this.isAnnualFormDueNow}}
                  <Text @variant="muted" @size="sm">
                    {{t "pslf.reimagined.hero.annual_reminder_due_now"}}
                  </Text>
                {{else}}
                  <Text @variant="muted" @size="sm">
                    {{t
                      "pslf.reimagined.hero.annual_reminder_due_soon"
                      days=this.annualReminderDaysRemaining
                    }}
                  </Text>
                {{/if}}
                <Button
                  class="mt-auto self-start"
                  @appearance="outlined"
                  @intent="primary"
                  data-test-pslf-annual-reminder-submit
                  {{on "click" (transitionTo "authenticated.pslf.dashboard.start-new")}}
                >
                  {{t "pslf.reimagined.hero.annual_reminder_cta"}}
                </Button>
              </Card>

              {{#if this.hasNsldsData}}
                <QualifyingPaymentTracker @accounts={{@person.accounts}} />
              {{else}}
                <UploadCtaCard @queryParams={{this.uploadQueryParams}} class="self-start" />
              {{/if}}

            {{else if this.isNoNslds}}
              <QualifyingPaymentTracker @accounts={{@person.accounts}} />

              <UploadCtaCard @queryParams={{this.uploadQueryParams}} />

            {{else}}
              <QualifyingPaymentTracker @accounts={{@person.accounts}} />

              <Card @padding="lg" class="tio-col-flush" data-test-estimator-card>
                <VStack class="p-0">
                  <Text
                    @variant="muted"
                    @size="xs"
                    @weight="semibold"
                    class="uppercase tracking-wider"
                  >
                    {{t "pslf.reimagined.hero.estimator_next_step"}}
                  </Text>
                  <Heading @level={{3}} @size="base">
                    {{t "pslf.reimagined.hero.estimator_title"}}
                  </Heading>
                  <Text @variant="muted" @size="sm">
                    {{t "pslf.reimagined.hero.estimator_description"}}
                  </Text>
                </VStack>
                <Button
                  class="mt-4 self-start"
                  @appearance="outlined"
                  @intent="primary"
                  data-test-get-estimate-button
                  {{on "click" this.goToEstimator}}
                >
                  {{t "pslf.reimagined.hero.estimator_cta"}}
                </Button>
              </Card>
            {{/if}}
          </div>
        </:default>
      </PageHero>

      {{#if this.annualSettings}}
        <AnnualSettingsDialog
          @isOpen={{this.isSettingsOpen}}
          @onClose={{this.closeSettings}}
          @status={{this.annualSettings}}
          @profile={{@pslfProfile}}
          @person={{@person}}
        />
      {{/if}}

    {{else}}
      <PageHero
        @gradient="brand-soft"
        class="tio-fg-primary tio-pslf-hero-icon tio-elevation-card-raised"
        ...attributes
      >
        <:icon>
          <PhHandCoins @size={{28}} />
          <Heading @level={{1}}>
            {{t "pslf.reimagined.hero.heading"}}
          </Heading>
        </:icon>
        <:default>
          <Text @size="lg" class="tio-fg-secondary max-w-2xl">
            {{t "pslf.reimagined.hero.description"}}
          </Text>
          <div class="mt-4 tio-row">
            <Button
              @intent="primary"
              data-test-pslf-start-new-form
              {{on "click" (transitionTo "authenticated.pslf.dashboard.start-new")}}
            >
              {{t "pslf.reimagined.hero.get_started"}}
            </Button>
          </div>
        </:default>
      </PageHero>
    {{/if}}
  </template>
}
